import { jwtDecode } from 'jwt-decode';

import { firebaseAuth } from '@client/services/firebase.config';
import axiosInstance from '@client/utils/axios.util';
import { logger } from '@client/utils/logger.util';

import type { User } from 'firebase/auth';

/**
 * Define the user's role based on their email domain.
 * @param {User} user - The Firebase user object.
 */
export const defineRole = async (user: User) => {
  await axiosInstance
    .post(
      '/auth/set-role',
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
      },
    )
    .catch((error) => {
      logger.error('Failed to set role', error);
    });
};

type FirebaseClaims = {
  role?: string;
  [key: string]: string | number | boolean;
};

/**
 * Get the current user's claims.
 * @param {User} user - The Firebase user object.
 * @returns {Promise<FirebaseClaims | null>} The claims object or null if no user is signed in.
 */
export const getUserClaims = async (
  user: User = firebaseAuth.currentUser,
): Promise<FirebaseClaims | null> => {
  try {
    const idToken = await getIdToken(user);
    if (!idToken) {
      throw new Error('No user signed in');
    }
    return jwtDecode<FirebaseClaims>(idToken);
  } catch (error) {
    logger.error('Failed to get claims', error);
    return null;
  }
};

/**
 * Get the current user's ID token.
 * @param {User} user - The Firebase user object.
 * @param {boolean} forceRefresh - Whether to force a refresh of the token.
 * @returns {Promise<string | null>} The ID token string or null if no user is signed in.
 */
export async function getIdToken(
  user: User = firebaseAuth.currentUser,
  forceRefresh: boolean = false,
): Promise<string | null> {
  try {
    if (!user) {
      throw new Error('No user signed in');
    }

    return user.getIdToken(forceRefresh);
  } catch (error) {
    logger.error('Failed to get claims', error);
    return null;
  }
}
